import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { PaginatedDto, SchoolStatus, SchoolSummaryDto } from '@eskoolhub/types';
import { PageHeader } from '../components/AppShell';
import { IconChevron, IconPlus, IconSearch } from '../components/Icons';
import { EmptyState, ErrorState, SchoolStatusChip, SchoolTile, SkeletonRows } from '../components/ui';
import { OnboardSchoolModal } from '../features/OnboardSchoolModal';
import { api, ApiRequestError } from '../lib/api';
import { formatDate, formatINRCompact, formatNumber, formatRate } from '../lib/format';

const FILTERS: { label: string; value: SchoolStatus | 'ALL' }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Trial', value: 'TRIAL' },
  { label: 'Payment issue', value: 'PAYMENT_ISSUE' },
  { label: 'Suspended', value: 'SUSPENDED' },
];

export function SchoolsPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<SchoolStatus | 'ALL'>('ALL');
  const [onboarding, setOnboarding] = useState(false);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['schools', query, status],
    queryFn: () =>
      api.get<PaginatedDto<SchoolSummaryDto>>(
        `/api/schools?pageSize=100${query ? `&query=${encodeURIComponent(query)}` : ''}${status === 'ALL' ? '' : `&status=${status}`}`,
      ),
    placeholderData: keepPreviousData,
  });

  const counts = data?.items.reduce<Record<string, number>>((acc, school) => {
    acc[school.status] = (acc[school.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <PageHeader
        title="Schools"
        meta={
          data
            ? `${data.total} total · ${counts?.ACTIVE ?? 0} active · ${counts?.TRIAL ?? 0} trial`
            : 'Loading…'
        }
        actions={
          <button className="btn" onClick={() => setOnboarding(true)}>
            <IconPlus fill="var(--ink)" />
            Onboard school
          </button>
        }
      />

      <div className="page">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              background: 'var(--white)',
              border: '1px solid var(--rule)',
              borderRadius: 12,
              padding: '0 14px',
              minHeight: 44,
              width: 320,
            }}
          >
            <IconSearch size={17} fill="var(--ink-soft)" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, code or city"
              style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, fontSize: 14 }}
            />
          </div>

          <div className="segmented" style={{ flex: 'none' }}>
            {FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                aria-pressed={status === filter.value}
                onClick={() => setStatus(filter.value)}
                style={{ padding: '10px 16px', flex: 'none' }}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {error ? (
          <ErrorState
            message={error instanceof ApiRequestError ? error.message : 'Could not load schools'}
            onRetry={() => void refetch()}
          />
        ) : isPending ? (
          <SkeletonRows count={5} />
        ) : data.items.length === 0 ? (
          <div className="card card--flush">
            <EmptyState
              title="No schools here yet"
              message={query || status !== 'ALL' ? 'Nothing matches that filter.' : 'Onboard the first school to get going.'}
              action={
                <button className="btn btn--sm" onClick={() => setOnboarding(true)}>
                  Onboard school
                </button>
              }
            />
          </div>
        ) : (
          <div className="card card--flush">
            <table className="table">
              <thead>
                <tr>
                  <th>School</th>
                  <th style={{ width: 130 }}>Plan</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Students</th>
                  <th style={{ width: 140, textAlign: 'right' }}>₹/student/mo</th>
                  <th style={{ width: 120, textAlign: 'right' }}>MRR</th>
                  <th style={{ width: 140 }}>Status</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {data.items.map((school) => (
                  <tr key={school.id} onClick={() => navigate(`/schools/${school.id}`)}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        <SchoolTile initials={school.initials} primary={school.status === 'ACTIVE'} />
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span style={{ fontSize: 14.5, fontWeight: 700 }}>{school.name}</span>
                          <span className="caption">
                            {school.city}
                            {school.onboardedAt
                              ? ` · onboarded ${formatDate(school.onboardedAt, { month: 'short', year: 'numeric' })}`
                              : ''}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 14 }}>
                      {school.planName ?? '—'}
                      {school.onTrial ? ' · trial' : ''}
                    </td>
                    <td className="num">{formatNumber(school.studentCount)}</td>
                    <td className="num">
                      {school.pricePerStudentPerMonthPaise === null ? (
                        <span style={{ color: 'var(--ink-soft)' }}>—</span>
                      ) : (
                        <>
                          {formatRate(school.pricePerStudentPerMonthPaise)}
                          {school.hasCustomPricing ? (
                            <span className="tag tag--manual" style={{ marginLeft: 6 }}>
                              Negotiated
                            </span>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td className="num" style={{ fontWeight: 600, color: school.mrrPaise === 0 ? 'var(--ink-soft)' : undefined }}>
                      {formatINRCompact(school.mrrPaise)}
                    </td>
                    <td>
                      <SchoolStatusChip status={school.status} trialDaysLeft={school.trialDaysLeft} />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <IconChevron fill="var(--ink-soft)" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {onboarding ? <OnboardSchoolModal onClose={() => setOnboarding(false)} /> : null}
    </>
  );
}
